import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const useNetGlobalShipping = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tries, setTries] = useState(0);

  const retry = () => {
    setTries((prev) => prev + 1);
  };

  const formatDateForNetGlobal = (date) => {
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };

    const formatted = new Intl.DateTimeFormat("en-US", options).format(date);

    return formatted.replace(",", "");
  };

  const getNetGlobalEssentials = async () => {
    setLoading(true);
    setError("");
    let result = {
      CompanyId: "",
      ServiceId: "",
      ServiceTypeId: "",
      ShipmentTypeId: "",
      SenderId: "",
      SenderAddressId: "",
      ContactId: "",
      Token: "",
    };

    const url = `${process.env.REACT_APP_NET_GLOBAL_URL}/api/authentication/loginClient`;
    const Params = {};

    try {
      const response = await axios.post(url, Params, {
        headers: {
          username: process.env.REACT_APP_NET_GLOBAL_USERNAME,
          password: process.env.REACT_APP_NET_GLOBAL_PASSWORD,
          Accept: "text/plain",
          "Content-Type": "text/plain"
        },
      });
      const shipmentType = response?.data?.shipmentTypeList?.find(
        (s) => s?.shipmentTypeName === "B2C Document"
      );
      result = {
        CompanyId: response?.data?.companyId ?? 8,
        ServiceId: response?.data?.serviceList[0]?.serviceId ?? 1,
        ServiceTypeId: response?.data?.serviceList[0]?.serviceTypeId ?? 1,
        ShipmentTypeId: shipmentType?.shipmentTypeId ?? 1,
        SenderId: response?.data?.shipperList[0]?.shipperId ?? 118395,
        SenderAddressId:
          response?.data?.shipperList[0]?.shipperAddress[0]?.shipperAddress
            ?.clientAddressId ?? 209559,
        ContactId:
          response?.data?.shipperList[0]?.shipperAddress[0]?.shipperContact[0]
            ?.clientContactId ?? 147018,
        Token: response?.data?.token,
      };
    } catch (err) {
      setError("Failed to create shipment.");
      setTimeout(() => {
        setError("");
      }, 5000);
    }
    setLoading(false);
    return result;
  };

  const createNetGlobalShipment = async (
    orderID,
    deliveryNotes,
    countryName,
    cityName,
    postalCode,
    addressLine1,
    addressLine2,
    customerName,
    customerEmail,
    customerPhone,
    description,
    pkgs,
    pkgDimensions
  ) => {
    // const settings = await getNetGlobalEssentials();
    setLoading(true);
    setError("");

    const today = new Date();
    let plannedShippingDate = new Date(today);
    plannedShippingDate.setDate(today.getDate() + 1);

    if (plannedShippingDate.getDay() === 0) {
      plannedShippingDate.setDate(plannedShippingDate.getDate() + 1);
    }

    const formattedPlannedShippingDate =
      formatDateForNetGlobal(plannedShippingDate);
    const formattedCreatedDate = formatDateForNetGlobal(today);

    const packages = [];
    for (const pkg of pkgs) {
      const Items = [];
      for (const prod of pkg?.products) {
        Items.push({
          Description: prod?.Prod?.attributes?.Description,
          HsCode: "19059090",
          SKU: prod?.Barcode,
          GoodsValue:
            prod?.Quantity *
            (prod?.Prod?.attributes?.discountedPrice !== null
              ? prod?.Prod?.attributes?.discountedPrice
              : prod?.Prod?.attributes?.Price),
          Currency: "AED",
          CountryOfOrigin: "United Arab Emirates",
          Quantity: prod?.Quantity,
          ProductURL:
            "www." +
            window.location.hostname +
            "/product/" +
            prod?.Prod?.attributes?.SEOURL,
        });
      }
      packages.push({
        SenderReference: pkg?.number,
        OrderReference: `#${orderID}`,
        CreatedDate: formattedCreatedDate,
        Length: pkgDimensions?.length,
        Width: pkgDimensions?.width,
        Height: pkgDimensions?.height,
        Weight: pkg?.weight,
        Items: Items,
      });
    }

    // Define the API endpoint and query parameters
    const url = `${process.env.REACT_APP_API_URL}net-create-shipment`;
    const params = {
      OrderTypeId: 1,
      OrderReference: `#${orderID}`,
      SenderReference: null,
      CompanyId: 8,
      ServiceId: 7,
      ServiceTypeId: 40,
      ShipmentTypeId: 19,
      CreatedDate: formattedCreatedDate,
      SenderId: 109575,
      SenderAddressId: 199820,
      ContactId: 137200,
      PickupSpecialInstructions: deliveryNotes,
      DeliverySpecialInstructions: deliveryNotes,
      ManifestId: 0,
      IsFulfillment: false,
      Receiver: {
        Name: customerName,
        Email: customerEmail,
        Address: {
          Country: countryName,
          City: cityName,
          Street: addressLine1,
          Building: "",
          Floor: "",
          LandMark: "",
          ZipCode: "",
          POBox: postalCode,
          GeoLocation: "", //location URL
          PhoneType: "phone", //phone/fax/mobil/whatsapp
          PhoneNumber: customerPhone,
          Ext: "",
          Contact: {
            ContactType: "Owner", //Owner
            ContactName: customerName,
            MobileNumber: customerPhone, //required
            Email: customerEmail,
          },
        },
      },
      OrderInternationalTypeId: 2, //1-High Value/ 2-Low Value
      ShipmentTermsId: 1, //1 - DDU  2 - DDP
      BusinessTypeId: 2, //1-B2B/2-B2C/3-C2C
      shipmentReason: "",
      Notes: description,
      EORI: "",
      Cod: [
        {
          Amount: 0.0,
          Currency: "AED",
          PaymentMethodId: 1, //1-Cash/2-Cheque/3-CCM
        },
      ],
      packages: packages,
    };

    if (addressLine2?.trim() != "") {
      params.Receiver.Address.Street += ", " + addressLine2;
    }

    let trackingNumber = "";
    let pdfDocument = "";
    let shippingResponse = "";
    try {
      const response = await axios.post(url, params);
      trackingNumber = response?.data?.OrderID;
      // pdfDocument = (response?.data?.URLLabel)?.replace("//", "//net.");
      pdfDocument = response?.data?.URLLabel;
      shippingResponse = response?.data || "Success but no data";
      console.log("NetGlobal Response: ", shippingResponse);
    } catch (err) {
      shippingResponse = err.response?.data || err.message || "Unknown Error";
      console.log("NetGlobal Error: ", shippingResponse);
      setError("Failed to create shipment.");
      setTimeout(() => {
        setError("");
      }, 5000);
    }
    setLoading(false);
    return [trackingNumber, pdfDocument, shippingResponse];
  };

  return {
    createNetGlobalShipment,
    loading,
    error,
    setError,
    retry,
  };
};

export default useNetGlobalShipping;
